/** Connection configured-state checks — shared by hooks, settings, and inbox. */

/**
 * Temporary product gate: show these channel cards as Coming soon.
 * Do not remove OAuth/configure handlers — flip kinds out of this set to re-enable.
 */
export const TEMP_COMING_SOON_CONNECTION_KINDS = new Set(["reddit", "quora"]);

function gmailConfigured(cfg, accountIdentifier) {
  const g = cfg.google_oauth || {};
  if (g.dev_mock) return false;
  if (!g.access_token_enc) return false;
  return !String(g.google_email || accountIdentifier || "").endsWith("@local.sellix");
}

function redditConfigured(cfg) {
  const r = cfg.reddit_oauth || {};
  if (r.dev_mock) return false;
  if (!r.access_token_enc && !r.refresh_token_enc) return false;
  return Boolean(r.reddit_username) && r.reddit_username !== "unknown";
}

function telegramConfigured(cfg) {
  const tg = cfg.telegram || {};
  if (!tg.bot_id || !tg.bot_token_enc) return false;
  const ingestMode = tg.ingest_mode || "openclaw_transport";
  if (ingestMode === "openclaw_transport" || ingestMode === "webhook_openclaw") {
    return Boolean(tg.openclaw_synced);
  }
  return true;
}

function quoraConfigured(cfg) {
  const q = cfg.quora || {};
  return Boolean(q.profile_url && q.display_name);
}

function metaConfigured(kind, cfg) {
  const m = cfg.meta || {};
  if (!m.page_access_token_enc) return false;
  if (kind === "facebook") return Boolean(m.page_id);
  if (kind === "instagram") return Boolean(m.instagram_business_account_id);
  return false;
}

function channelConfigured(kind, cfg, accountIdentifier) {
  if ((kind === "gmail" || cfg.google_oauth) && !gmailConfigured(cfg, accountIdentifier)) {
    return false;
  }
  if ((kind === "reddit" || cfg.reddit_oauth) && !redditConfigured(cfg)) {
    return false;
  }
  if ((kind === "telegram" || cfg.telegram) && !telegramConfigured(cfg)) {
    return false;
  }
  if ((kind === "facebook" || kind === "instagram") && !metaConfigured(kind, cfg)) {
    return false;
  }
  return !(kind === "quora" || cfg.quora) || quoraConfigured(cfg);
}

export function isConfiguredStatus(status, configuredFlag, accountIdentifier, channelConfig, kind) {
  try {
    if (typeof configuredFlag === "boolean") return configuredFlag;
    if (status !== "connected") return false;
    if (accountIdentifier && /\(mock\)/i.test(String(accountIdentifier))) return false;

    const cfg = channelConfig && typeof channelConfig === "object" ? channelConfig : {};
    return channelConfigured(kind, cfg, accountIdentifier);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error("[connections] isConfiguredStatus failed", { kind, status, error: e });
    return false;
  }
}

/** UI status for Settings → Connections cards. */
export function connectionDisplayStatus(row, seeding = false, kind = null) {
  const resolvedKind = kind ?? row?.kind ?? null;
  // Temporary UI gate — Reddit/Quora connect flows remain implemented underneath.
  if (resolvedKind && TEMP_COMING_SOON_CONNECTION_KINDS.has(resolvedKind)) {
    return "coming_soon";
  }
  if (!row) return seeding ? "seeding" : "not_seeded";
  if (row.status === "sync_failed") return "sync_failed";
  if (isConfiguredStatus(row.status, row.configured, row.account_identifier, row.channel_config, row.kind)) {
    return "connected";
  }
  if (row.status === "pairing" && isPairingSessionActive(row)) {
    return "pairing";
  }
  if (row.status === "error") return "error";
  return "unpaired";
}

/** True when a WhatsApp (or similar) QR pairing session is still live. */
export function isPairingSessionActive(row, nowMs = Date.now()) {
  if (!row || row.status !== "pairing") return false;
  const pairing = row.channel_config?.pairing;
  if (!pairing || typeof pairing !== "object") return false;

  const expiresAt = Date.parse(pairing.expires_at || "");
  if (Number.isFinite(expiresAt)) {
    return expiresAt > nowMs;
  }

  const startedAt = Date.parse(pairing.started_at || "");
  if (!Number.isFinite(startedAt)) return false;
  const graceMs = 15 * 60 * 1000;
  return nowMs - startedAt <= graceMs;
}

/**
 * Whether the card should show the primary action when status is connected.
 * Product UX: connected cards show Disconnect only — never Manage / Reconnect / Reconfigure.
 * Labels and handlers below remain so these actions can be re-enabled later.
 */
export function shouldShowConnectedChannelAction(_kind) {
  return false;
}

/**
 * Primary action label for a connection card button.
 * Connected integrations must not show "Connect".
 * Connected Manage/Reconnect/Reconfigure labels are retained for future UI re-enable;
 * {@link shouldShowConnectedChannelAction} currently hides that button for all kinds.
 */
export function getConnectionActionLabel(status, kind) {
  // Only TEMP_COMING_SOON_CONNECTION_KINDS use this status — keep other kinds unchanged.
  if (status === "coming_soon") return "Coming soon";
  if (status === "sync_failed") return "Retry Sync";
  if (status === "connected") {
    if (kind === "gmail" || kind === "reddit") return "Reconnect";
    if (kind === "whatsapp") return "Manage";
    if (kind === "telegram" || kind === "linkedin" || kind === "quora") return "Reconfigure";
    return "Manage";
  }
  if (status === "pairing") return "Complete Setup";
  if (status === "error") return "Reconnect";
  if (kind === "whatsapp") return "Pair";
  return "Connect";
}

/** Whether the primary action should use filled (default) button styling. */
export function isConnectionActionPrimary(status) {
  return status !== "connected" && status !== "coming_soon";
}

/** True when Telegram bot is saved but OpenClaw sync is still pending. */
export function isTelegramSyncPending(row) {
  if (!row || row.kind !== "telegram") return false;
  const tg = row.channel_config?.telegram || {};
  return Boolean(tg.bot_token_enc) && tg.openclaw_synced === false;
}
